import * as React from "react";
import { Award } from "lucide-react";
import prompt from "../../../config/prompts/tier3-final-eligibility-validation.md";
import { SentActionIndicator } from "../SentActionIndicator";

/**
 * The message the Draft Certificate action sends: the Tier 3 final eligibility
 * validation prompt, verbatim.
 *
 * It is sent as the user's message and stays in the conversation history like
 * any other. The prompt is self-contained on purpose - it tells the model to
 * treat the request as the first thing it was asked and to use nothing but the
 * screenings it carries - which is what lets it be re-run after the document
 * has been changed, as many times as it takes to get a clean certificate.
 */
export const DRAFT_CERTIFICATE_PROMPT: string = prompt;

/** Label shared by the offer card and the bubble that stands in for the message. */
export const DRAFT_CERTIFICATE_LABEL = "Generate a Draft Certificate";

const DRAFT_CERTIFICATE_DESCRIPTION =
  "Run the Tier 3 final eligibility validation on the contract as it stands now.";

/**
 * Accent shared by the card and the bubble, so the two read as one action. The
 * Prompt Coach's next-steps purple, since that is where the card sits.
 */
const ACCENT = "#7C3AED";
const ACCENT_BORDER = "#9333EA";
const BADGE_GRADIENT = "linear-gradient(135deg, #7C3AED 0%, #C4B5FD 100%)";

/** Stands in for the prompt in the user's bubble. */
export function DraftCertificateIndicator() {
  return <SentActionIndicator Icon={Award} label={DRAFT_CERTIFICATE_LABEL} accent={ACCENT} />;
}

interface DraftCertificateCardProps {
  onClick: () => void;
}

/**
 * The offer to generate the certificate.
 *
 * Sits as the last card in the Prompt Coach's next steps, and on its own under
 * an answer that has no Prompt Coach - the certificate itself ends without
 * one, and the offer has to survive that so the certificate can be re-run.
 * Styled after the next-step cards it sits among, with enough of its own
 * identity to read as the workflow's destination rather than one more
 * suggestion.
 */
export function DraftCertificateCard({ onClick }: DraftCertificateCardProps) {
  const [hovered, setHovered] = React.useState(false);

  const cardStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 14,
    textAlign: "left",
    padding: "14px 16px",
    background: hovered ? "linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)" : "#FFFFFF",
    border: `2px solid ${hovered ? ACCENT_BORDER : "#E5E7EB"}`,
    borderRadius: 20,
    cursor: "pointer",
    boxShadow: hovered ? "0 4px 14px rgba(124, 58, 237, 0.18)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
    transform: hovered ? "translateY(-1px)" : "translateY(0)",
    transition: "all 200ms ease-out",
    fontFamily: "'Segoe UI', sans-serif",
    outline: "none",
  };

  const badgeStyle: React.CSSProperties = {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: BADGE_GRADIENT,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(124, 58, 237, 0.35)",
  };

  const titleStyle: React.CSSProperties = {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    color: hovered ? ACCENT : "#111827",
    lineHeight: 1.4,
    transition: "color 200ms ease-out",
  };

  const descriptionStyle: React.CSSProperties = {
    display: "block",
    marginTop: 2,
    fontSize: 12,
    color: "#6B7280",
    lineHeight: 1.5,
  };

  const arrowStyle: React.CSSProperties = {
    flexShrink: 0,
    fontSize: 16,
    color: ACCENT_BORDER,
    opacity: hovered ? 1 : 0,
    transition: "opacity 200ms ease-out",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      aria-label={DRAFT_CERTIFICATE_LABEL}
      style={cardStyle}
    >
      <span style={badgeStyle}>
        <Award size={20} color="#FFFFFF" strokeWidth={2} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={titleStyle}>{DRAFT_CERTIFICATE_LABEL}</span>
        <span style={descriptionStyle}>{DRAFT_CERTIFICATE_DESCRIPTION}</span>
      </span>
      <span style={arrowStyle} aria-hidden="true">
        →
      </span>
    </button>
  );
}
